'use client';

import { AlertTriangle, FileQuestion, Home, RefreshCw } from 'lucide-react';
import Link from 'next/link';

type PageUnavailableProps = {
    variant?: 'not-found' | 'error';
    onRetry?: () => void;
};

export default function PageUnavailable({ variant = 'not-found', onRetry }: PageUnavailableProps) {
    const isNotFound = variant === 'not-found';
    const Icon = isNotFound ? FileQuestion : AlertTriangle;

    return (
        <main className="relative isolate flex min-h-[100svh] items-center justify-center overflow-hidden bg-slate-950 px-6 py-16 text-white">
            <div aria-hidden="true" className="absolute inset-0 -z-10">
                <div className="absolute left-1/2 top-[-18rem] h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-indigo-500/25 blur-3xl" />
                <div className="absolute bottom-[-16rem] right-[-10rem] h-[30rem] w-[30rem] rounded-full bg-cyan-400/15 blur-3xl" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:28px_28px]" />
            </div>

            <section className="w-full max-w-2xl text-center">
                <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-3xl border border-white/10 bg-white/10 shadow-2xl shadow-indigo-950/50 backdrop-blur">
                    <Icon aria-hidden="true" className="h-9 w-9 text-indigo-300" strokeWidth={1.7} />
                </div>

                <div className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-semibold tracking-[0.18em] text-indigo-200">
                    {isNotFound ? '404 · PAGE NOT FOUND' : 'TEMPORARILY UNAVAILABLE'}
                </div>

                <h1 className="text-balance text-4xl font-bold tracking-tight text-white sm:text-6xl">{isNotFound ? 'This page isn’t available' : 'We couldn’t load this page'}</h1>
                <p className="mx-auto mt-5 max-w-xl text-pretty text-base leading-7 text-slate-300 sm:text-lg">
                    {isNotFound
                        ? 'The page may be unpublished, moved, or the address may be incorrect. Please check the URL or return to the homepage.'
                        : 'A temporary problem prevented the page from loading. Please try again in a moment.'}
                </p>

                <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <Link
                        href="/"
                        className="inline-flex min-w-44 items-center justify-center gap-2 rounded-xl bg-indigo-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 transition hover:bg-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-2 focus:ring-offset-slate-950"
                    >
                        <Home aria-hidden="true" className="h-4 w-4" />
                        Go to homepage
                    </Link>
                    {onRetry && (
                        <button
                            type="button"
                            onClick={onRetry}
                            className="inline-flex min-w-44 items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-slate-950"
                        >
                            <RefreshCw aria-hidden="true" className="h-4 w-4" />
                            Try again
                        </button>
                    )}
                </div>

                {isNotFound && <p className="mt-10 text-sm text-slate-500">If you manage this page, check its publishing settings.</p>}
            </section>
        </main>
    );
}
